import classNames from 'classnames';
import React, { forwardRef, useMemo } from 'react';
import Icon, { IconSize } from '@/components/atoms/icon/icon';
import Spinner from '@/components/atoms/spinner/spinner';
import Link, { LinkProps } from '@/components/utilities/link/link';
import styles from './button.module.scss';

export type ButtonVariant = 'ghost' | 'primary' | 'secondary' | 'tertiary';
export type ButtonRenderAs = 'div';

export interface ButtonProps {
  'aria-label'?: string;
  /** The class name to apply */
  className?: string;
  /** Indicates whether the button is disabled */
  disabled?: boolean;
  /** Indicates whether the button is focused */
  focused?: boolean;
  /** Indicates whether the button is hovered */
  hovered?: boolean;
  /** The icon to show in the button */
  icon?: IconName;
  /** The position of the icon relative to the text */
  iconPosition?: 'left' | 'right';
  /** The size of the icon to show in the button */
  iconSize?: IconSize;
  /** Indicates whether this is used in a highlight-section */
  isHighlightSection?: boolean;
  /** Indicates whether the button should show a loading state */
  isLoading?: boolean;
  /** Indicates whether the button is a small variation */
  isSmall?: boolean;
  /** An event that is triggered on click */
  onClick?: LinkProps['onClick'];
  /** Prefetch */
  prefetch?: boolean | null;
  /** The element to render when no interactive semantics are needed */
  renderAs?: ButtonRenderAs;
  /** The tab index of the component */
  tabIndex?: number;
  /** The target attribute for the button used as link */
  target?: LinkProps['target'];
  /** The text to show */
  text?: string;
  /** The type of button to use */
  type?: React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLAnchorElement | HTMLButtonElement>,
    HTMLAnchorElement | HTMLButtonElement
  >['type'];
  /** The url to link to */
  url?: string;
  /** The variant style to use for the button */
  variant?: ButtonVariant;
}

const variants: Record<ButtonVariant, string> = {
  ghost: styles.isGhost,
  primary: styles.isPrimary,
  secondary: styles.isSecondary,
  tertiary: styles.isTertiary,
};

const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      disabled = false,
      focused = false,
      hovered = false,
      icon,
      iconPosition = 'right',
      iconSize,
      isHighlightSection = false,
      isLoading,
      isSmall,
      onClick,
      renderAs,
      target,
      text,
      type,
      url,
      variant = 'primary',
      ...rest
    },
    ref,
  ) => {
    const handleClick: LinkProps['onClick'] = event => {
      if (isLoading) {
        event?.preventDefault();

        return;
      }

      onClick?.(event);
    };

    const buttonContent = useMemo(() => {
      if (isLoading) {
        return (
          <div className={styles.spinnerWrapper}>
            <Spinner hasTransparentOverlay isSmall loadingText={undefined} />
          </div>
        );
      }

      const iconElement = icon && (
        <Icon name={icon} size={iconSize ? iconSize : isSmall ? 12 : 24} />
      );

      return (
        <React.Fragment>
          {iconPosition === 'left' && iconElement}

          {text}

          {iconPosition === 'right' && iconElement}
        </React.Fragment>
      );
    }, [icon, iconPosition, iconSize, isLoading, isSmall, text]);

    const buttonClassName = classNames(
      styles.component,
      variants[variant],
      {
        [styles.isDisabled]: disabled ?? !url,
        [styles.isFocused]: focused,
        [styles.isHighlightSection]: isHighlightSection,
        [styles.isHovered]: hovered,
        [styles.isIconButton]: !text && icon,
        [styles.isSmall]: isSmall,
      },
      className,
    );

    if (renderAs === 'div') {
      return (
        <div
          {...rest}
          ref={ref as React.ForwardedRef<HTMLDivElement>}
          className={buttonClassName}
        >
          {buttonContent}
        </div>
      );
    }

    return (
      <Link
        {...rest}
        ref={ref}
        className={buttonClassName}
        onClick={handleClick}
        rel={url && target === '_blank' ? 'noreferrer' : undefined}
        target={target}
        type={type}
        url={url}
      >
        {buttonContent}
      </Link>
    );
  },
);

Button.displayName = 'Button';

export default Button;
